import CricketFieldEngine from "../../field-engine/CricketFieldEngine";
import { useRef, useState } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Operator-supplied 39-position spec (2026-05-16). Same names the wagon-
// wheel resolver returns when the operator clicks a region, so the
// dropdown and the wheel always agree. Grouped by ring for the dropdown.
const FIELDING_POSITIONS = [
  // Behind / very close (Inner ring)
  "Wicket Keeper", "Bowler",
  "Slips", "Gully", "Leg Slip", "Leg Gully",
  "Silly Point", "Short Cover", "Short Extra Cover", "Silly Mid Off",
  "Silly Mid On", "Short Mid Wicket", "Short Leg", "Short Backward Square Leg",
  // 30-yard ring
  "Mid Off", "Extra Cover", "Cover", "Cover Point", "Point", "Backward Point",
  "Short Third", "Short Fine Leg", "Backward Square Leg", "Square Leg",
  "Mid Wicket", "Mid On",
  // Boundary
  "Straight", "Long Off", "Deep Extra Cover", "Deep Cover",
  "Deep Point", "Deep Backward Point", "Third Man", "Long Stop",
  "Deep Fine Leg", "Long Leg", "Deep Backward Square Leg", "Deep Square Leg",
  "Deep Mid Wicket", "Cow Corner", "Long On",
];

const FIELDING_ACTIONS = [
  "Fielded",
  "Runs Saved",
  "Runs Misfielded",
  "Overthrow",
  "Caught",
  "Catch Assist",
  "Dropped",
  "Run Out- Direct Hit, Out",
  "Run Out- Direct Hit, Not-Out",
  "Run Out- Throw, Out",
  "Run Out- Throw, Not-Out",
  "Run Out- Assist",
  "Run Out- Missed Assist",
  "Run Out- Missed Stumps",
  "Stumped",
  "Stumping Missed",
];

const FIELDING_DIFFICULTIES = ["Easy", "Medium", "Hard"];

export default function SpatialPicker({
  ball,
  setBall,
  players,
  currentBowlerId,
  wicketKeeperId,
  batterHand = "RIGHT",
  structuredRegion,
  onStructuredRegionChange,
  subFielders = [],          // Squad reserves — bowling team's MatchSquad minus PlayingXI
}) {
  const [fieldingModalOpen, setFieldingModalOpen] = useState(false);
  const fieldingPanelRef = useRef(null);
  useDialogDismiss({
    open: fieldingModalOpen,
    onClose: () => setFieldingModalOpen(false),
    ref: fieldingPanelRef,
  });
  // Quick lookup so we can flip is_substitute=true when a sub is picked.
  const subFielderIds = new Set((subFielders || []).map((p) => p.id));

  // Native <optgroup> label text inherits the browser's default colour
  // (often dark) but uses the SELECT's open-panel background, which on
  // our dark theme renders as white text on white = invisible. Force
  // explicit dark-grey text + soft bg via inline style so the group
  // labels read clearly in both Chrome / Safari / Firefox.
  const optgroupStyle = {
    background: "#1f2937",     // slate-800 — sits clearly above bg-secondary
    color: "#fef3c7",          // amber-100 — high contrast for label text
    fontStyle: "normal",
    fontWeight: 700,
  };

  // 2026-06-01 — fielder picker now shows the jersey number in the
  // same "Name (33)" format the BallByBallPage uses for batter +
  // bowler badges (founder ask: jersey visible across all fielder
  // dropdowns + wagon wheel pick). Players without a jersey number
  // fall through to the plain name.
  const labelFor = (p) => {
    const baseName =
      p?.nick_name
      || p?.name
      || [p?.first_name, p?.last_name].filter(Boolean).join(" ")
      || "Unknown";
    const jersey =
      p?.jersey_number != null && String(p.jersey_number).trim() !== ""
        ? String(p.jersey_number).trim()
        : null;
    return jersey ? `${baseName} (${jersey})` : baseName;
  };

  // Renders an XI optgroup + a Substitutes optgroup so the operator can
  // pick a reserve as a fielder. Used everywhere a fielder is selected.
  // When subFielders is empty (operator hasn't added any to the team's
  // master squad yet), render a disabled hint option that points them
  // at the Teams → Squad modal where the Add Player UI lives — without
  // that nudge, the picker looked permanently limited to the XI.
  const renderFielderOptions = () => (
    <>
      <optgroup label="— Playing XI —" style={optgroupStyle}>
        {(players?.bowlers ?? []).map((p) => (
          <option key={p.id} value={p.id} className="bg-bg-secondary text-text-primary">
            {labelFor(p)}
          </option>
        ))}
      </optgroup>
      {(subFielders || []).length > 0 ? (
        <optgroup label="— Substitute fielders (squad reserves) —" style={optgroupStyle}>
          {(subFielders || []).map((p) => (
            <option key={p.id} value={p.id} className="bg-bg-secondary text-text-primary">
              {labelFor(p) + " (sub)"}
            </option>
          ))}
        </optgroup>
      ) : (
        <optgroup label="— No substitutes registered —" style={optgroupStyle}>
          <option value="" disabled className="bg-bg-secondary text-text-secondary italic">
            Add players via Teams → Squad to surface them here
          </option>
        </optgroup>
      )}
    </>
  );

  // Fielder picker is always available regardless of stroke / shot-
  // connection. Even "Padded", "No shot", "Hit Body" balls can still
  // have a fielder picked (the keeper takes a deflection, a fielder
  // collects a leg-bye, etc.). All wagon-wheel selections are optional;
  // the operator leaves the dropdowns blank if nothing applies.
  const noBatContact = false;
  // Boundary 4/6 — no fielder applies because the ball cleared the rope.
  // We hide the fielding panel and switch the wagon-wheel into "click
  // outside the rope" mode so the operator can mark exactly where the
  // ball crossed.
  const isBoundary = !!ball?.is_boundary;

  // Boundary balls: fielder + structured_position are now OPTIONAL but
  // PRESERVED — the operator may want to record who got close to the
  // ball as it crossed the rope (boundary saves, dives, etc.). No
  // auto-clear; the operator can leave the dropdowns blank or fill
  // them in as they see fit.

  const clearFielder = () => {
    setBall((prev) => ({
      ...prev,
      fielding: {
        ...prev.fielding,
        fielders: [
          {
            player: null,
            action: "",
            fielding_position: "",
            runs_saved: null,
            runs_misfielded: null,
            overthrow_runs: null,
            difficulty: null,
          },
          ...((prev.fielding?.fielders || []).slice(1)),
        ],
      },
    }));
  };

  // -------------------------
  // FIELDERS HELPERS
  // -------------------------
  const addFielder = () => {
    setBall((prev) => ({
      ...prev,
      fielding: {
        ...prev.fielding,
        fielders: [
          ...(prev.fielding?.fielders || []),
          {
            player: null,
            action: "",
            fielding_position: "",
            runs_saved: null,
            runs_misfielded: null,
            overthrow_runs: null,
            difficulty: null,
          },
        ],
      },
    }));
  };

  const updateFielder = (index, key, value) => {
    setBall((prev) => {
      const fielders = [...(prev.fielding?.fielders || [])];
      let f = { ...fielders[index], [key]: value };

      // 🔥 CASE 1: PLAYER SELECTED
      if (key === "player") {
        if (value) {
          // ✅ Always default action
          f.action = "Fielded";

          // ✅ Auto position
          if (value === currentBowlerId) {
            f.fielding_position = "Bowler";
          } else if (value === wicketKeeperId) {
            f.fielding_position = "Wicket Keeper";
          }

          // ✅ Flag substitute fielders so the scorecard / commentary
          // can render "Name (sub)". When the picked id is in the
          // bowling-team squad's reserves (subFielders list), is_substitute
          // flips to true; picking back an XI player clears it.
          f.is_substitute = subFielderIds.has(value);
        } else {
          // reset everything
          f = {
            player: null,
            action: "",
            fielding_position: "",
            runs_saved: null,
            runs_misfielded: null,
            overthrow_runs: null,
            difficulty: null,
            is_substitute: false,
          };
        }
      }

      // 🔥 CASE 2: ACTION CHANGE
      if (key === "action") {
        f.runs_saved = null;
        f.runs_misfielded = null;
        f.overthrow_runs = null;
        f.difficulty = null;

        if (value === "Runs Saved") f.runs_saved = 0;
        if (value === "Runs Misfielded") f.runs_misfielded = 0;
        if (value === "Overthrow") f.overthrow_runs = 0;
      }

      fielders[index] = f;

      return {
        ...prev,
        fielding: {
          ...prev.fielding,
          fielders,
        },
      };
    });
  };

  const removeFielder = (index) => {
    setBall((prev) => {
      const fielders = [...(prev.fielding?.fielders || [])];
      fielders.splice(index, 1);

      return {
        ...prev,
        fielding: { ...prev.fielding, fielders },
      };
    });
  };

  // -------------------------
  // AUTO FIELDING LOGIC
  // -------------------------
 

  // -------------------------
  // FIELD ENGINE
  // -------------------------
  const selectedRegionId = structuredRegion?.regionId ?? null;

  const handleStructuredSelect = (region) => {
    onStructuredRegionChange?.(region);

    if (region?.position) {
      setBall((prev) => ({
        ...prev,
        spatial: {
          ...prev.spatial,
          structured_region_id: region.regionId ?? null,
          structured_slice_index: region.sliceIndex ?? null,
          structured_band_index: region.bandIndex ?? null,
          // Position is always recorded — even on boundaries it's useful
          // for "the ball went past Deep Mid Wicket" analytics. The
          // commentary / scorecard already handles boundaries separately.
          structured_position: region.position ?? "",
          // Capture the exact click point too so analytics + heatmaps have
          // sub-zone resolution. Coords are in viewBox space (0..100).
          wagon_wheel_x: region.x ?? prev.spatial?.wagon_wheel_x ?? null,
          wagon_wheel_y: region.y ?? prev.spatial?.wagon_wheel_y ?? null,
        },
        // Pre-fill the first fielder's position from the click, but keep
        // any operator-picked player / action / runs values intact. The
        // fielder dropdown stays optional — the operator can leave the
        // player blank if no fielder was actually involved.
        fielding: {
          ...prev.fielding,
          fielders: [
            {
              ...(prev.fielding?.fielders?.[0] || {}),
              fielding_position: region.position,
            },
          ],
        },
      }));
    }
  };

  // -------------------------
  // UI
  // -------------------------
  return (
    <div className="mt-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* FIELD ENGINE — sm:2/3 width, fielding panel renders on the right
            for every ball (boundary clicks can still optionally pick a
            fielder — useful for boundary saves / dives / overthrows).
            The outer column reserves a min-height so the wheel never
            gets cramped; the inner box is forced square (aspect-square)
            with a sensible max-width so the SVG (viewBox 100×100) always
            renders as a full circle — never clipped against the panel
            edges (was 256px-fixed before; on wider parents the wheel
            had big horizontal gutters and the boundary labels could
            overlap the container edge, was reported by an operator
            picking 4s / 6s on a mid-sized desktop).
            Phones: stack vertically and shrink min-height so the wheel
            doesn't push the fielding panel off the viewport. */}
        <div className="w-full sm:w-2/3 flex items-center justify-center min-h-[16rem] sm:min-h-[24rem]">
          <div className="w-full max-w-[20rem] sm:max-w-[28rem] aspect-square bg-bg-main/60 rounded-xl border border-bg-secondary p-2">
            <CricketFieldEngine
              batterHand={batterHand}
              selectedRegionId={selectedRegionId}
              onRegionSelect={handleStructuredSelect}
              pinX={ball?.spatial?.wagon_wheel_x ?? null}
              pinY={ball?.spatial?.wagon_wheel_y ?? null}
              isBoundary={isBoundary}
            />
          </div>
        </div>

        {/* FIELDING PANEL — visible for every ball except when the ball
            never came off the bat (Padded, Shoulder Arms, etc.). For
            boundaries the picker stays available so the operator can
            optionally record who saved / dived / overthrew. */}
        {noBatContact ? (
          <div className="w-full sm:w-1/3 flex items-center justify-center text-center px-3 py-4 rounded border border-dashed border-bg-secondary text-[11px] text-text-secondary leading-snug">
            <span>
              Fielder n/a — ball didn't come off the bat
              ({ball?.analytics?.shot_connection || ball?.analytics?.stroke}).
            </span>
          </div>
        ) : (
        <div className="w-full sm:w-1/3 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-text-secondary mr-1">
              Quick set:
            </span>
            <button
              onClick={() => {
                if (wicketKeeperId) {
                  updateFielder(0, "player", wicketKeeperId);
                } else {
                  // No flagged keeper in the XI — still set the position so the
                  // operator can pick the player from the dropdown below.
                  updateFielder(0, "fielding_position", "Wicket Keeper");
                }
              }}
              className="text-[11px] px-2 py-1 bg-bg-secondary rounded hover:bg-bg-main"
              title="Set the keeper as the fielder (stumpings, behind-the-wicket takes). If no keeper is flagged in the XI, the position is set so you can pick the player below."
            >
              Keeper took it
            </button>
            <button
              onClick={() => currentBowlerId && updateFielder(0, "player", currentBowlerId)}
              disabled={!currentBowlerId}
              className="text-[11px] px-2 py-1 bg-bg-secondary rounded hover:bg-bg-main disabled:opacity-40 disabled:cursor-not-allowed"
              title="Set the bowler as the fielder (caught-and-bowled, bowler fielding)"
            >
              Bowler fielded
            </button>
            <button
              onClick={clearFielder}
              disabled={!ball.fielding?.fielders?.[0]?.player}
              className="text-[11px] px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:border-bg-secondary disabled:text-text-secondary/40"
              title="Clear the current fielder selection"
            >
              ✕ Clear
            </button>
          </div>

          {(() => {
            const f = ball.fielding?.fielders?.[0] || {};

            return (
              <div className="border p-2 rounded">
                {/* PLAYER */}
                <select
                  value={f.player ?? ""}
                  onChange={(e) =>
                    updateFielder(0, "player", e.target.value || null)
                  }
                  className="w-full mb-2"
                >
                  <option
                    value=""
                    className="bg-bg-secondary text-text-primary px-2 py-1"
                  >
                    None
                  </option>
                  {renderFielderOptions()}
                </select>
                {f.is_substitute && (
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-2">
                    Substitute fielder — will appear as "(sub)" on the scorecard
                  </div>
                )}

                {/* POSITION */}
                <select
                  value={f.fielding_position ?? ""}
                  onChange={(e) =>
                    updateFielder(0, "fielding_position", e.target.value)
                  }
                  className="w-full mb-2"
                >
                  <option
                    value=""
                    className="bg-bg-secondary text-text-primary px-2 py-1"
                  >
                    Position
                  </option>
                  {FIELDING_POSITIONS.map((pos) => (
                    <option
                      key={pos}
                      className="bg-bg-secondary text-text-primary px-2 py-1"
                    >
                      {pos}
                    </option>
                  ))}
                </select>

                {/* ACTION */}
                <select
                  value={f.action ?? ""}
                  onChange={(e) => updateFielder(0, "action", e.target.value)}
                  className="w-full mb-2"
                >
                  <option
                    value=""
                    className="bg-bg-secondary text-text-primary px-2 py-1"
                  >
                    Action
                  </option>
                  {FIELDING_ACTIONS.map((a) => (
                    <option
                      key={a}
                      className="bg-bg-secondary text-text-primary px-2 py-1"
                    >
                      {a}
                    </option>
                  ))}
                </select>

                {/* 🔥 DYNAMIC INPUTS */}
                {f.action === "Runs Saved" && (
                  <input
                    type="number"
                    value={f.runs_saved ?? 0}
                    onChange={(e) =>
                      updateFielder(0, "runs_saved", Number(e.target.value))
                    }
                    className="w-full mb-2"
                    placeholder="Runs Saved"
                  />
                )}

                {f.action === "Runs Misfielded" && (
                  <input
                    type="number"
                    value={f.runs_misfielded ?? 0}
                    onChange={(e) =>
                      updateFielder(
                        0,
                        "runs_misfielded",
                        Number(e.target.value),
                      )
                    }
                    className="w-full mb-2"
                    placeholder="Runs Misfielded"
                  />
                )}

                {/* 2026-05-30 — overthrow_runs input restored (founder
                    ask: bring it back). Note for the operator: this is
                    ANALYTICS ONLY. The total runs (off-bat + overthrow)
                    are entered in the main score row at the top; this
                    field just records how many of those runs came from
                    a fielding error so the QC / scorecard can break it
                    down. Backend no longer adds this to aggregate.runs
                    (the score is already in the main row), so entering
                    a value here won't double-count. */}
                {f.action === "Overthrow" && (
                  <>
                    <input
                      type="number"
                      value={f.overthrow_runs ?? 0}
                      onChange={(e) =>
                        updateFielder(0, "overthrow_runs", Number(e.target.value))
                      }
                      min={0}
                      max={6}
                      className="w-full mb-1"
                      placeholder="Overthrow Runs (analytics only)"
                    />
                    <div className="text-[10px] text-text-secondary mb-2 italic">
                      Total runs go in the score row above — this field
                      records how many of those came from a fielding error.
                    </div>
                  </>
                )}

                {(f.action === "Caught" || f.action === "Dropped") && (
                  <>
                    <select
                      value={f.difficulty ?? ""}
                      onChange={(e) =>
                        updateFielder(0, "difficulty", e.target.value || null)
                      }
                      className="w-full mb-2"
                    >
                      <option value="">Difficulty</option>
                      {FIELDING_DIFFICULTIES.map((difficulty) => (
                        <option key={difficulty} value={difficulty}>
                          {difficulty}
                        </option>
                      ))}
                    </select>
                    {/* Substitute-fielder tracking — Law 24. When checked, the
                        scorecard renders "c sub b <bowler>" instead of the
                        fielder's name. Optional "replacing" pick records which
                        regular XI player the sub is filling in for. */}
                    <label className="flex items-center gap-2 text-[11px] text-text-secondary mb-2">
                      <input
                        type="checkbox"
                        checked={!!f.is_substitute}
                        onChange={(e) =>
                          updateFielder(0, "is_substitute", e.target.checked)
                        }
                      />
                      <span>Substitute fielder (renders as <code>c sub b …</code>)</span>
                    </label>
                    {f.is_substitute && (
                      <select
                        value={f.replacing_player ?? ""}
                        onChange={(e) =>
                          updateFielder(0, "replacing_player", e.target.value || null)
                        }
                        className="w-full mb-2"
                      >
                        <option value="">Replacing… (optional)</option>
                        {(players?.bowlers ?? []).map((p) => (
                          <option key={p.id} value={p.id} className="bg-bg-secondary text-text-primary">
                            {labelFor(p)}
                          </option>
                        ))}
                      </select>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* OPEN MODAL */}
          <button
            onClick={() => setFieldingModalOpen(true)}
            className="text-xs px-3 py-1 rounded bg-bg-secondary"
          >
            More Fielders
          </button>
        </div>
        )}
      </div>

      {/* MODAL */}
      {fieldingModalOpen && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) setFieldingModalOpen(false); }}
        >
          <div
            ref={fieldingPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Fielders"
            className="bg-bg-primary p-4 rounded-xl w-[420px] max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between mb-3">
              <h3>Fielders</h3>
              <button onClick={() => setFieldingModalOpen(false)}>✕</button>
            </div>

            {(ball.fielding?.fielders || []).map((f, idx) => (
              <div key={idx} className="border p-2 rounded mb-3">
                <select
                  value={f.player ?? ""}
                  onChange={(e) =>
                    updateFielder(idx, "player", e.target.value || null)
                  }
                  className="w-full mb-2"
                >
                  <option value="">Player</option>
                  {renderFielderOptions()}
                </select>
                {f.is_substitute && (
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-2">
                    Substitute fielder — will render as "(sub)"
                  </div>
                )}

                <select
                  value={f.fielding_position ?? ""}
                  onChange={(e) =>
                    updateFielder(idx, "fielding_position", e.target.value)
                  }
                  className="w-full mb-2"
                >
                  <option value="">Position</option>
                  {FIELDING_POSITIONS.map((pos) => (
                    <option key={pos} className="bg-black">
                      {pos}
                    </option>
                  ))}
                </select>

                <select
                  value={f.action ?? ""}
                  onChange={(e) => updateFielder(idx, "action", e.target.value)}
                  className="w-full mb-2"
                >
                  <option value="">Action</option>
                  {FIELDING_ACTIONS.map((a) => (
                    <option key={a} className="bg-black">
                      {a}
                    </option>
                  ))}
                </select>

                {f.action === "Runs Saved" && (
                  <input
                    type="number"
                    value={f.runs_saved ?? 0}
                    onChange={(e) =>
                      updateFielder(idx, "runs_saved", Number(e.target.value))
                    }
                    className="w-full mb-2"
                  />
                )}

                {f.action === "Runs Misfielded" && (
                  <input
                    type="number"
                    value={f.runs_misfielded ?? 0}
                    onChange={(e) =>
                      updateFielder(
                        idx,
                        "runs_misfielded",
                        Number(e.target.value),
                      )
                    }
                    className="w-full mb-2"
                  />
                )}

                {f.action === "Overthrow" && (
                  <input
                    type="number"
                    value={f.overthrow_runs ?? 0}
                    onChange={(e) =>
                      updateFielder(
                        idx,
                        "overthrow_runs",
                        Number(e.target.value),
                      )
                    }
                    className="w-full mb-2"
                  />
                )}

                {(f.action === "Caught" || f.action === "Dropped") && (
                  <select
                    value={f.difficulty ?? ""}
                    onChange={(e) =>
                      updateFielder(idx, "difficulty", e.target.value || null)
                    }
                    className="w-full mb-2"
                  >
                    <option value="">Difficulty</option>
                    {FIELDING_DIFFICULTIES.map((difficulty) => (
                      <option key={difficulty} value={difficulty} className="bg-black">
                        {difficulty}
                      </option>
                    ))}
                  </select>
                )}

                <button
                  onClick={() => removeFielder(idx)}
                  className="text-red-400 text-xs"
                >
                  Remove
                </button>
              </div>
            ))}

            <button
              onClick={addFielder}
              className="w-full bg-bg-secondary p-2 rounded"
            >
              + Add Fielder
            </button>

            <button
              onClick={() => setFieldingModalOpen(false)}
              className="w-full mt-3 bg-green-700 p-2 rounded text-white"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
